import {
  Object3D,
  Color,
  OrthographicCamera,
  BoxGeometry,
  Mesh,
  Sprite,
  Vector3,
  MeshBasicMaterial,
  CanvasTexture,
  SpriteMaterial,
  Vector4,
} from 'three';

class ViewHelper extends Object3D {
  constructor(editorCamera, container) {
    super();

    this.animating = false;
    this.controls = null;

    const color1 = new Color('#ff3653');
    const color2 = new Color('#8adb00');
    const color3 = new Color('#2c8fff');

    const interactiveObjects = [];
    
    // Create the camera for the helper
    const camera = this.camera = new OrthographicCamera(-2, 2, 2, -2, 0, 4);
    camera.position.set(0, 0, 2);

    const geometry = new BoxGeometry(0.8, 0.05, 0.05).translate(0.4, 0, 0);

    const xAxis = new Mesh(geometry, this.getAxisMaterial(color1));
    const yAxis = new Mesh(geometry, this.getAxisMaterial(color2));
    const zAxis = new Mesh(geometry, this.getAxisMaterial(color3));

    yAxis.rotation.z = Math.PI / 2;
    zAxis.rotation.y = -Math.PI / 2;

    // Add to this (ViewHelper extends Object3D)
    this.add(xAxis);
    this.add(zAxis);
    this.add(yAxis);

    const posXAxisHelper = new Sprite(this.getSpriteMaterial(color1, 'X'));
    posXAxisHelper.userData.type = 'posX';
    const posYAxisHelper = new Sprite(this.getSpriteMaterial(color2, 'Y'));
    posYAxisHelper.userData.type = 'posY';
    const posZAxisHelper = new Sprite(this.getSpriteMaterial(color3, 'Z'));
    posZAxisHelper.userData.type = 'posZ';
    const negXAxisHelper = new Sprite(this.getSpriteMaterial(color1));
    negXAxisHelper.userData.type = 'negX';
    const negYAxisHelper = new Sprite(this.getSpriteMaterial(color2));
    negYAxisHelper.userData.type = 'negY';
    const negZAxisHelper = new Sprite(this.getSpriteMaterial(color3));
    negZAxisHelper.userData.type = 'negZ';

    posXAxisHelper.position.x = 1;
    posYAxisHelper.position.y = 1;
    posZAxisHelper.position.z = 1;
    negXAxisHelper.position.x = -1;
    negXAxisHelper.scale.setScalar(0.8);
    negYAxisHelper.position.y = -1;
    negYAxisHelper.scale.setScalar(0.8);
    negZAxisHelper.position.z = -1;
    negZAxisHelper.scale.setScalar(0.8);

    // Add sprites to this
    this.add(posXAxisHelper);
    this.add(posYAxisHelper);
    this.add(posZAxisHelper);
    this.add(negXAxisHelper);
    this.add(negYAxisHelper);
    this.add(negZAxisHelper);

    interactiveObjects.push(posXAxisHelper);
    interactiveObjects.push(posYAxisHelper);
    interactiveObjects.push(posZAxisHelper);
    interactiveObjects.push(negXAxisHelper);
    interactiveObjects.push(negYAxisHelper);
    interactiveObjects.push(negZAxisHelper);

    // Store references
    this.editorCamera = editorCamera;
    this.container = container;
    this.point = new Vector3();
    this.dim = 150;
    
    // Store sprite references
    this.posXAxisHelper = posXAxisHelper;
    this.posYAxisHelper = posYAxisHelper;
    this.posZAxisHelper = posZAxisHelper;
    this.negXAxisHelper = negXAxisHelper;
    this.negYAxisHelper = negYAxisHelper;
    this.negZAxisHelper = negZAxisHelper;
  }

  getAxisMaterial(color) {
    return new MeshBasicMaterial({ color: color, toneMapped: false });
  }

  getSpriteMaterial(color, text = null) {
    const canvas = document.createElement('canvas');
    canvas.width = 64;
    canvas.height = 64;

    const context = canvas.getContext('2d');
    context.beginPath();
    context.arc(32, 32, 16, 0, 2 * Math.PI);
    context.closePath();
    context.fillStyle = color.getStyle();
    context.fill();

    if (text !== null) {
      context.font = '24px Arial';
      context.textAlign = 'center';
      context.fillStyle = '#000000';
      context.fillText(text, 32, 41);
    }

    const texture = new CanvasTexture(canvas);

    return new SpriteMaterial({ map: texture, toneMapped: false });
  }

  render(renderer) {
    // Sync the helper's rotation with the main camera
    this.quaternion.copy(this.editorCamera.quaternion).invert();
    this.updateMatrixWorld();

    this.point.set(0, 0, 1);
    this.point.applyQuaternion(this.editorCamera.quaternion);

    // Update sprite opacities based on camera direction
    if (this.point.x >= 0) {
      this.posXAxisHelper.material.opacity = 1;
      this.negXAxisHelper.material.opacity = 0.5;
    } else {
      this.posXAxisHelper.material.opacity = 0.5;
      this.negXAxisHelper.material.opacity = 1;
    }

    if (this.point.y >= 0) {
      this.posYAxisHelper.material.opacity = 1;
      this.negYAxisHelper.material.opacity = 0.5;
    } else {
      this.posYAxisHelper.material.opacity = 0.5;
      this.negYAxisHelper.material.opacity = 1;
    }

    if (this.point.z >= 0) {
      this.posZAxisHelper.material.opacity = 1;
      this.negZAxisHelper.material.opacity = 0.5;
    } else {
      this.posZAxisHelper.material.opacity = 0.5;
      this.negZAxisHelper.material.opacity = 1;
    }

    // Store current renderer state
    const currentViewport = renderer.getViewport(new Vector4());
    const currentScissor = renderer.getScissor(new Vector4());
    const currentScissorTest = renderer.getScissorTest();

    // Clear depth buffer and set viewport for the helper
    renderer.clearDepth();
    renderer.setViewport(0, 0, this.dim, this.dim);
    renderer.setScissor(0, 0, this.dim, this.dim);
    renderer.setScissorTest(true);
    
    // Render this ViewHelper object with its own camera
    renderer.render(this, this.camera);

    // Restore renderer state
    renderer.setViewport(currentViewport);
    renderer.setScissor(currentScissor);
    renderer.setScissorTest(currentScissorTest);
  }
}

ViewHelper.prototype.isViewHelper = true;

export { ViewHelper };